import { LIST, MAP } from 'catchment/constants/CatchmentConstant'
import Input from 'components/forms/Input'
import SieauAction from 'components/sieau/SieauAction'
import { push } from '@lagunovsky/redux-react-router'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { nbPerPageLabel } from 'referencial/constants/ReferencialConstants'
import i18n from 'simple-react-i18n'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import { getLabel } from 'utils/StoreUtils'
import { normalizeString } from 'utils/StringUtil'
import CatchmentPointsMap from '../CatchmentPointsMap'
import useSandreList from 'utils/customHook/useSandreList'
import Checkbox from 'components/forms/Checkbox'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import SimpleFilterSelect from 'components/forms/specific/SimpleFilterSelect'
import { Button, Card, CardContent, Grid, Chip, Stack } from '@mui/material'
import { CardTable, sortRows } from 'components/datatable/NewTable'
import SimpleTabList from 'components/list/SimpleTabList'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import { favOrange } from 'utils/constants/ColorTheme'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'
import { exportFile } from 'utils/ExportDataUtil'
import { sortBy } from 'lodash'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import { SHOW_INSEE_CODE, SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import WatermassAction from 'referencial/components/watermass/actions/WatermassAction'
import CatchmentsIndicators from './CatchmentsIndicators'
import CatchmentPointDto from 'catchment/dto/CatchmentPointDto'

const NO_FILTER = -1

const PriorityChip = ({
    title,
    color = 'default',
}) => {
    return (
        <Chip
            label={title}
            size={'small'}
            color={color}
            sx={{ mr: 1, maxWidth: '16px', maxHeight: '16px', minHeight: '16px', minWidth: '16px', fontSize: '8px',
                '& .MuiChip-label': {
                    padding: 0,
                },
            }}

        />
    )
}

PriorityChip.propTypes = {
    title: PropTypes.string,
    color: PropTypes.string,
}

const FilterFields = ({
    defaultFilters = {},
    onValidate = () => {},
}) => {
    const {
        catchments,
        departments,
        watermasses,
    } = useSelector(store => ({
        catchments: store.CatchmentReducer.catchments,
        departments: store.CityReducer.departments,
        watermasses: store.WatermassReducer.watermasses,
    }), shallowEqual)

    const [filters, setFilters] = useState(defaultFilters)
    const [reset, setReset] = useState(false)

    const sdages = useSandreList('CAPTAGES.SDAGE')

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const watermassList = useMemo(() => {
        return watermasses.filter(w => w.euroCode && catchments.some(c => c.meCode === w.euroCode)).map(w => ({
            code: w.euroCode,
            name: `${showSandreCode ? `[${w.euroCode}]` : ''} ${w.name}`,
        }))
    }, [catchments, watermasses, showSandreCode])

    const onCancel = () => {
        setReset(filters.filter !== NO_FILTER)
        setFilters({
            searchValue: '',
            filter: NO_FILTER,
            resultFilter: [],
            selectedDepartments: [],
            selectedSdages: [],
            selectedReferenceSdages: [],
            selectedWatermass: [],
            selectedPriorities: [],
            displayActiveSDAGE: false,
            useOnlyMainPoint: false,
        })
    }

    return (
        <Card>
            <CardContent>
                <Grid container spacing={1} alignItems='center'>
                    <Grid size={4}>
                        <Input
                            tooltip={i18n.searchThenEnter}
                            title={i18n.search}
                            value={filters.searchValue}
                            onChange={v => setFilters({ ...filters, searchValue: v })}
                            onEnterKeyPress={(value, clearFunc) => {
                                setFilters({ ...filters, searchValue: value })
                                if (clearFunc !== 'clearFunc') {
                                    onValidate(filters)
                                }
                            }}
                            clearFunction
                            data-cy='search'
                        />
                    </Grid>
                    <Grid size={4}>
                        <SimpleFilterSelect
                            stationType={STATION_TYPE_NAME.catchment}
                            onChange={(results, newFilter) => {
                                setReset(false)
                                setFilters({ ...filters, filter: newFilter, resultFilter: results })
                            }}
                            stations={catchments}
                            data-cy='filter'
                            value={reset ? NO_FILTER : filters.filter}
                        />
                    </Grid>
                    <Grid size={4}>
                        <SuperMultiAutocomplete
                            label={i18n.department}
                            options={departments}
                            keyValue='code'
                            onChange={v => setFilters({ ...filters, selectedDepartments: v })}
                            values={filters.selectedDepartments}
                            multiple
                            limit={2}
                        />
                    </Grid>
                    <Grid size={4}>
                        <SuperMultiAutocomplete
                            label={i18n.sdage}
                            options={sdages}
                            keyValue='code'
                            onChange={v => setFilters({ ...filters, selectedSdages: v })}
                            values={filters.selectedSdages}
                            multiple
                            limit={1}
                        />
                    </Grid>
                    <Grid size={4}>
                        <SuperMultiAutocomplete
                            label={i18n.referenceSDAGE}
                            options={sdages}
                            keyValue='code'
                            onChange={v => setFilters({ ...filters, selectedReferenceSdages: v })}
                            values={filters.selectedReferenceSdages}
                            multiple
                            limit={1}
                        />
                    </Grid>
                    <Grid size={4}>
                        <Checkbox
                            label={i18n.displayActiveSDAGE}
                            checked={filters.displayActiveSDAGE}
                            onChange={v => setFilters({ ...filters, displayActiveSDAGE: v })}
                        />
                    </Grid>
                    <Grid size={8}>
                        {watermasses.length && (
                            <SuperMultiAutocomplete
                                label={i18n.watermass}
                                options={watermassList}
                                keyValue='code'
                                onChange={v => setFilters({ ...filters, selectedWatermass: v })}
                                values={filters.selectedWatermass}
                                multiple
                                limit={1}
                                additionalKeysFilter={['code']}
                            />
                        ) || <></>}
                    </Grid>
                    <Grid size={4}>
                        <SuperMultiAutocomplete
                            label={i18n.catchmentType}
                            options={[
                                { code: 'priority', name: i18n.havePriority },
                                { code: 'sensitive', name: i18n.sensitive },
                            ]}
                            keyValue='code'
                            onChange={v => setFilters({ ...filters, selectedPriorities: v })}
                            values={filters.selectedPriorities}
                            multiple
                        />
                    </Grid>
                    <Grid size={8}>
                        <Checkbox
                            label={i18n.useOnlyDataMainPoint}
                            checked={filters.useOnlyMainPoint}
                            onChange={v => setFilters({ ...filters, useOnlyMainPoint: v })}
                        />
                    </Grid>
                    <Grid offset={'auto'} size='auto' >
                        <Button
                            variant='outlined'
                            fullWidth
                            onClick={onCancel}
                        >
                            { i18n.reinit }
                        </Button>
                    </Grid>
                    <Grid size='auto' >
                        <Button
                            variant='contained'
                            onClick={() => onValidate(filters)}
                            data-cy='submit'
                        >
                            {i18n.search}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

FilterFields.propTypes = {
    defaultFilters: PropTypes.shape({}),
    onValidate: PropTypes.func,
}

const CatchmentTable = ({
    catchments = [],
    catchmentPoints = [],
}) => {
    const dispatch = useDispatch()
    const {
        contributors,
        departments,
        selectedSearchValues,
        userBookmarks,
    } = useSelector(store => ({
        contributors: store.ContributorReducer.contributors,
        departments: store.CityReducer.departments,
        selectedSearchValues: store.AdministrationReducer.selectedSearchValues,
        userBookmarks: store.UserReducer.userBookmarks,
    }), shallowEqual)

    const generalShowInseeCode = useApplicationSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : true)
    const showInseeCode = useAccountSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : generalShowInseeCode)

    const catchmentBookmarks = useMemo(() => {
        return userBookmarks.filter(b => b.stationType === 11).map(b => b.identifiant)
    }, [userBookmarks])

    const catchmentFormated = catchments.map(catchment => {
        const points = catchmentPoints.filter(p => p.id === catchment.id)
        const mainPoint = points.find(p => p.mainPoint)
        const buildingOwner = mainPoint?.owner || (!!points.length && points.every(p => p.owner === points[0].owner) && points[0].owner)
        const isFav = catchmentBookmarks.some(code => code === catchment.code)
        return {
            id: catchment.id,
            nullValue: { value: isFav ? <StarRoundedIcon sx={{ color: favOrange }} /> : undefined, noPadding: true },
            nullValuePriorities: {
                value:
                <>
                    { catchment.priority && <PriorityChip title={'P'} color='error'/> || undefined }
                    { catchment.sensitive && <PriorityChip title={'S'} color='warning'/> || undefined }
                </>,
            },
            department: { value: getLabel(departments, catchment.department, showInseeCode ? 'labelWithCode' : 'name') },
            departmentCode: catchment.department,
            code: { value: catchment.code },
            catchment: { value: catchment.name },
            mainCatchmentPoint: { value: mainPoint?.code },
            nbPoint: { value: points.length },
            grenelle: { value: catchment.grenelleWork ? i18n.yes : i18n.no },
            buildingOwner: { value: buildingOwner ? getLabel(contributors, buildingOwner, 'labelDisplay', 'id') : '' },
            aac: { value: catchment.aacNum },
        }
    })
    const catchmentsOrdered = sortBy(catchmentFormated, ['departmentCode', 'catchment.value'])

    const { catchment: searchValues } = selectedSearchValues

    const onTableSort = (sort) => {
        dispatch(SieauAction.update('selectedSearchValues', 'catchment', { stations: sortRows(catchmentFormated, sort), sort }))
    }

    const exportAction = {
        icon: 'file_download',
        tooltip: i18n.export,
        onClick: () => {
            const exportDataFormated = catchments.map(catchment => {
                const points = catchmentPoints.filter(p => p.id === catchment.id)
                const mainPoint = points.find(p => p.mainPoint)
                const buildingOwner = mainPoint?.owner || (!!points.length && points.every(p => p.owner === points[0].owner) && points[0].owner)
                const isFav = catchmentBookmarks.some(code => code === catchment.code)
                return {
                    bookmark: { value: isFav ? i18n.yes : i18n.no },
                    havePriority: { value: catchment.priority ? i18n.yes : i18n.no },
                    sensitive: { value: catchment.sensitive ? i18n.yes : i18n.no },
                    department: { value: getLabel(departments, catchment.department, showInseeCode ? 'labelWithCode' : 'name') },
                    departmentCode: catchment.department,
                    code: { value: catchment.code, cellType: 'left' },
                    catchment: { value: catchment.name },
                    mainCatchmentPoint: { value: mainPoint?.code, cellType: 'left' },
                    nbPoint: { value: points.length, cellType: 'left' },
                    grenelle: { value: catchment.grenelleWork ? i18n.yes : i18n.no },
                    buildingOwner: { value: buildingOwner ? getLabel(contributors, buildingOwner, 'labelDisplay', 'id') : '' },
                    aac: { value: catchment.aacNum },
                }
            })
            const exportDataOrdered = sortBy(exportDataFormated, ['departmentCode', 'catchment.value'])
            const exportData = exportDataOrdered.length ? [{ ...exportDataOrdered[0], headers: ['bookmark', 'havePriority', 'sensitive', 'department', 'code', 'catchment', 'mainCatchmentPoint', 'nbPoint', 'grenelle', 'buildingOwner', 'aac'] }, ...exportDataOrdered.slice(1)] : []
            exportFile({
                data: exportData,
                exportType: 'xlsx',
                titleFile: `${i18n.catchments}`,
            })
        },
    }

    return (
        <CardTable
            title={i18n.catchments}
            actions={[exportAction]}

            rows={catchmentsOrdered}
            headers={['nullValue', 'nullValuePriorities', 'department', 'code', 'catchment', 'mainCatchmentPoint', 'nbPoint', 'grenelle', 'buildingOwner', 'aac']}

            onClickRow={s => dispatch(push(`/station/catchment/${s.id}`))}
            defaultSort={searchValues?.sort}
            onSort={onTableSort}
            rowsPerPageOptions={nbPerPageLabel}

            data-cy='catchments_table'
        />
    )
}

CatchmentTable.propTypes = {
    catchments: PropTypes.arrayOf(PropTypes.shape({})),
    catchmentPoints: PropTypes.arrayOf(PropTypes.instanceOf(CatchmentPointDto)),
}

const CatchmentsDashboardApp = () => {
    const {
        catchments,
        catchmentPoints,
        selectedSearchValues,
    } = useSelector(store => ({
        catchments: store.CatchmentReducer.catchments,
        catchmentPoints: store.CatchmentReducer.catchmentPoints,
        selectedSearchValues: store.AdministrationReducer.selectedSearchValues,
    }), shallowEqual)

    const [filters, setFilters] = useState({
        searchValue: selectedSearchValues.catchment?.searchValue || '',
        filter: NO_FILTER,
        resultFilter: [],
        selectedDepartments: [],
        selectedSdages: [],
        selectedReferenceSdages: [],
        selectedWatermass: [],
        selectedPriorities: [],
        displayActiveSDAGE: false,
        useOnlyMainPoint: false,
    })

    const dispatch = useDispatch()

    useTitle(() => [{
        title: i18n.catchments,
        href: 'catchment',
    }, {
        title: i18n.dashboard,
        href: 'catchment',
    }], [])

    useActions(() => ({
        new: () => dispatch(push('/station/catchment/new/description')),
    }), [])

    useEffect(() => {
        dispatch(WatermassAction.fetchWatermasses())
    }, [])

    const catchmentsFiltered = useMemo(() => {
        const {
            filter,
            resultFilter,
            displayActiveSDAGE,
            selectedSdages,
            searchValue,
            selectedPriorities,
            selectedDepartments,
            selectedReferenceSdages,
            selectedWatermass,
        } = filters

        const listCatchments = filter === NO_FILTER ? catchments : resultFilter
        const searchValueFormated = normalizeString(searchValue)
        const searchActiveSDAGE = displayActiveSDAGE ? listCatchments.filter(({ activeSdage }) => activeSdage) : listCatchments
        const watermassFilter = selectedWatermass.length ? searchActiveSDAGE.filter(c => filters.selectedWatermass.includes(c.meCode)) : searchActiveSDAGE
        const departmentFilter = selectedDepartments.length ? watermassFilter.filter(c => filters.selectedDepartments.includes(c.department)) : watermassFilter
        const searchValueFilter = searchValueFormated ? departmentFilter.filter(({ name, code, aacNum }) => normalizeString(`${name}${code}${aacNum}`).includes(searchValueFormated)) : departmentFilter
        const prioritiesFilter = selectedPriorities.length ? searchValueFilter.filter(c => ((c.priority && selectedPriorities.includes('priority')) || (c.sensitive && selectedPriorities.includes('sensitive')))) : searchValueFilter
        return selectedSdages.length ? prioritiesFilter.filter(({ usedSdage }) => selectedSdages.includes(usedSdage) || (selectedReferenceSdages.length && selectedReferenceSdages.includes(usedSdage))) : prioritiesFilter
    }, [filters, catchments])

    const catchmentPointFiltered = useMemo(() => {
        return catchmentsFiltered.flatMap(c => {
            const points = catchmentPoints.filter(p => c.id === p.id)
            if (points.length === 0) return points
            if (filters.useOnlyMainPoint) {
                const mainPoint = points.find(p => p.mainPoint)
                return mainPoint ? [mainPoint] : [points[0]]
            }
            return points
        })
    }, [catchmentsFiltered, filters.useOnlyMainPoint, catchmentPoints])

    return (
        <Stack spacing={1} sx={{ marginTop: '10px', marginRight: '10px' }}>
            <FilterFields defaultFilters={filters} onValidate={setFilters} />
            <CatchmentsIndicators catchments={catchmentsFiltered} catchmentPoints={catchmentPointFiltered} />
            <SimpleTabList
                defaultTab={LIST}
                tabs={[
                    {
                        constant: LIST,
                        label: i18n.table,
                        icon: 'list',
                    },
                    {
                        constant: MAP,
                        label: i18n.map,
                        icon: 'map',
                    },
                ]}
                noPadding
            >
                {tab => (
                    <>
                        {tab === LIST && (<CatchmentTable catchments={catchmentsFiltered} catchmentPoints={catchmentPointFiltered} />)}
                        {tab === MAP && (<CatchmentPointsMap catchmentPoints={catchmentPointFiltered} />)}
                    </>
                )}
            </SimpleTabList>
        </Stack>
    )
}

export default CatchmentsDashboardApp